import { EventType } from "@/analytics/analytics-events";
import { useAppSelector, useAppStore } from "@/store/store-hooks";
import { selectCurrentUser } from "@/store/user-selectors";
import { useToast } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { GUID } from "@faro-lotv/foundation";
import { isRevisionScanEntity } from "@faro-lotv/service-wires";
import {
  createContext,
  JSX,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { selectPartOfConnectedComponents } from "./store/data-preparation-ui/data-preparation-ui-selectors";
import { selectRevisionEntityMap } from "./store/revision/revision-entity-selectors";
import { selectIsAddingConnections } from "./store/user-edits/data-preparation-user-edit-selectors";
import {
  addUserEditAddConnection,
  setEditMode,
} from "./store/user-edits/data-preparation-user-edit-slice";
import { RegistrationEditMode } from "./store/user-edits/user-edit-types";

type EditConnectionContextData = {
  /**
   * @param entityId The ID of the entity that was clicked.
   * @returns true, if the click was handled as adding a new connection.
   * False if the click still needs to be handled.
   */
  handleAddConnection(entityId: GUID | undefined): boolean;

  /** The ID of the first scan of the connection, when it is currently being added. */
  sourceId?: GUID;
};

/**  */
const EditConnectionContext = createContext<
  EditConnectionContextData | undefined
>(undefined);

/**
 * @returns Data for handling the editing of connections, especially adding new connection lines.
 */
export function useEditConnectionContext(): EditConnectionContextData {
  const ctx = useContext(EditConnectionContext);
  if (!ctx) {
    throw Error(
      "useEditConnectionContext called outside an EditConnectionContext",
    );
  }
  return ctx;
}

/**
 * @returns A context for managing the edit connection workflow.
 *  Keeps track of which scans have to be connected.
 */
export function EditConnectionContextProvider({
  children,
}: PropsWithChildren): JSX.Element {
  const { getState, dispatch } = useAppStore();
  const { openToast } = useToast();
  const [sourceId, setSourceId] = useState<GUID | undefined>(undefined);

  const handleAddConnection = useCallback(
    (entityId: GUID | undefined): boolean => {
      if (!entityId) return false;

      const state = getState();

      const isAddingConnections = selectIsAddingConnections(state);
      const currentUser = selectCurrentUser(state);
      if (!isAddingConnections || !currentUser) return false;

      const entityMap = selectRevisionEntityMap(state);
      const entity = entityMap[entityId];
      if (!entity) return false;

      // If clicking on the same scan again, toggle it off
      if (entityId === sourceId) {
        setSourceId(undefined);
        return true;
      }

      // Connections can only be created between captures
      if (!isRevisionScanEntity(entity)) {
        openToast({
          title: "Connections can only be created between captures.",
          variant: "warning",
        });
        setSourceId(undefined);
        return true;
      }

      if (!sourceId) {
        setSourceId(entityId);
        return true;
      }

      const targetId = entityId;

      const partOfGroups = selectPartOfConnectedComponents(state);
      if (
        partOfGroups.some(
          (group) => group.includes(sourceId) && group.includes(targetId),
        )
      ) {
        openToast({
          title:
            "Cannot create a connection between scans of the same capture.",
          variant: "warning",
        });
        setSourceId(undefined);
        return true;
      }

      // Add connection
      Analytics.track(EventType.addRegistrationConnection);
      dispatch(
        addUserEditAddConnection({
          sourceId,
          targetId,
          createdBy: currentUser.id,
        }),
      );
      // Exit add connection mode and return to edit scans mode
      dispatch(setEditMode(RegistrationEditMode.editScans));
      setSourceId(undefined);

      return true;
    },
    [sourceId, getState, dispatch, openToast],
  );

  const isAddingConnections = useAppSelector(selectIsAddingConnections);

  // React to edit mode changes
  useEffect(() => {
    setSourceId(undefined);
  }, [isAddingConnections]);

  const value = useMemo(
    () => ({
      sourceId,
      handleAddConnection,
    }),
    [sourceId, handleAddConnection],
  );

  return (
    <EditConnectionContext value={value}>{children}</EditConnectionContext>
  );
}
